import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import { http, ACCESS_KEY } from "../components/resources/resourceUtils";
import { ResourceLibraryCard } from "../components/resources/ResourceLibraryCard";
import { VideoLightbox } from "../components/resources/VideoLightbox";
import { AccessGate } from "../components/resources/AccessGate";
import "../styles/resources.css";

const DEFAULT_INTRO =
    "A collection of resources to support your work between sessions. If anything here brings up more than you expected, reach out to me directly.";

const CategoryChips = ({ categories, active, onChange }) => (
    <div className="flex flex-wrap gap-2.5" role="group" aria-label="Filter by category" data-testid="resource-category-filters">
        {[{ id: "all", name: "All" }, ...categories].map((c) => (
            <button
                key={c.id}
                type="button"
                className="res-chip"
                aria-pressed={active === c.id}
                onClick={() => onChange(c.id)}
                data-testid={`resource-filter-${c.id === "all" ? "all" : c.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {c.name}
            </button>
        ))}
    </div>
);

function useResources() {
    const [state, setState] = useState({ loading: true, locked: false, intro: DEFAULT_INTRO, categories: [], resources: [] });
    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true }));
        const { data: settings } = await http.get("/client-resources/settings").catch(() => ({ data: {} }));
        const intro = settings.intro || DEFAULT_INTRO;
        if (!settings.access_code_enabled) localStorage.removeItem(ACCESS_KEY);
        const token = localStorage.getItem(ACCESS_KEY);
        try {
            const { data } = await http.get("/client-resources", { headers: token ? { "X-Resources-Access": token } : {} });
            setState({ loading: false, locked: false, intro, categories: data.categories, resources: data.resources });
        } catch (e) {
            if (e?.response?.status === 403) localStorage.removeItem(ACCESS_KEY);
            setState({ loading: false, locked: e?.response?.status === 403, intro, categories: [], resources: [] });
        }
    }, []);
    useEffect(() => { load(); }, [load]);
    return { ...state, reload: load };
}

export default function ResourcesPage() {
    const { loading, locked, intro, categories, resources, reload } = useResources();
    const [active, setActive] = useState("all");
    const [query, setQuery] = useState("");
    const [watching, setWatching] = useState(null);

    const catName = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);
    const usedCats = categories.filter((c) => resources.some((r) => r.category_id === c.id));
    const featured = resources.filter((r) => r.featured).slice(0, 3);
    const q = query.trim().toLowerCase();
    const filtered = resources.filter(
        (r) =>
            (active === "all" || r.category_id === active) &&
            (!q || r.title.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q))
    );
    const showFeatured = featured.length > 0 && active === "all" && !q;

    return (
        <main className="ap-res min-h-screen">
            <Seo
                title="Client Resources | Alchemy Practice"
                description="Worksheets, guided practices, videos and reading shared by Alchemy Practice to support your therapy between sessions."
            />
            <Nav />
            <section className="pt-40 pb-14 lg:pt-48 lg:pb-16" data-testid="resources-hero">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                    <span className="res-eyebrow">Between sessions</span>
                    <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-[72px] leading-[1.04]" style={{ color: "#4A4A30" }} data-testid="resources-heading">
                        Client <em className="italic" style={{ color: "#8A9470" }}>Resources</em>
                    </h1>
                    <div className="res-gold-rule mt-8" />
                    <p className="mt-8 max-w-2xl text-[18px] leading-[1.8]" style={{ color: "#5C5C47" }} data-testid="resources-intro">{intro}</p>
                </div>
            </section>

            <section className="pb-24 lg:pb-32" data-testid="resources-library">
                <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                    {loading ? (
                        <p className="text-[16px]" style={{ color: "#5C5C47" }} data-testid="resources-loading">Loading resources...</p>
                    ) : locked ? (
                        <AccessGate onUnlocked={reload} />
                    ) : (
                        <>
                            {showFeatured && (
                                <div className="mb-20" data-testid="resources-featured">
                                    <span className="res-eyebrow">Featured</span>
                                    <div className="mt-6 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                                        {featured.map((r) => (
                                            <ResourceLibraryCard key={r.id} r={r} featured categoryName={catName[r.category_id]} onWatch={setWatching} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:justify-between">
                                <CategoryChips categories={usedCats} active={active} onChange={setActive} />
                                <div className="relative w-full lg:w-[340px]">
                                    <Search size={18} strokeWidth={1.5} className="absolute left-5 top-1/2 -translate-y-1/2" style={{ color: "#5E6748" }} aria-hidden />
                                    <label htmlFor="resource-search" className="sr-only">Search resources</label>
                                    <input
                                        id="resource-search"
                                        type="search"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search resources"
                                        className="res-input"
                                        data-testid="resource-search-input"
                                    />
                                </div>
                            </div>
                            {filtered.length === 0 ? (
                                <p className="mt-16 text-[17px] italic font-serif" style={{ color: "#5C5C47" }} data-testid="resources-empty">
                                    {resources.length === 0 ? "New resources are on the way." : "Nothing matches that search yet."}
                                </p>
                            ) : (
                                <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
                                    {filtered.map((r) => (
                                        <ResourceLibraryCard key={r.id} r={r} categoryName={catName[r.category_id]} onWatch={setWatching} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    <p className="mt-24 pt-8 border-t text-[15px] leading-[1.7] max-w-2xl" style={{ borderColor: "#E8E4DA", color: "#5C5C47" }} data-testid="resources-crisis-note">
                        These resources support your therapy and are not a substitute for care. If you are in crisis, call or text 988.
                    </p>
                </div>
            </section>
            <VideoLightbox resource={watching} onClose={() => setWatching(null)} />
            <Footer />
        </main>
    );
}
